import type { ReactNode } from 'react'
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  CardHeaderActions,
} from '@/components/Card'
import styles from './CapturedInformationCard.module.css'
import { TabBar, TabItem } from '@/components/TabBar'

import { Tooltip } from '@/components/Tooltip'
import { IconButton } from '@/components/IconButton'
import { FormGroup } from '@/components/Form'
import { TextArea } from '@/components/inputs/TextArea'
import { Button } from '@/components/Button'
import { type CapturedInformationTab } from '@/features/jobs/details/DetailsViewModel'

export interface CapturedInformationCardTab {
  id: CapturedInformationTab
  label: string
  testId: string
  highlighted?: boolean
}

export interface CapturedInformationCardProps {
  showEditButton: boolean
  onEdit: () => unknown
  tabs: CapturedInformationCardTab[]
  activeTab: CapturedInformationTab
  onTabChange: (tab: CapturedInformationTab) => unknown
  children?: ReactNode
}

/**
 * DOM ID of the captured information card, for scrolling it into view.
 */
export const capturedInformationCardId = 'captured-information-card'

export function CapturedInformationCard({
  showEditButton,
  onEdit,
  tabs,
  activeTab,
  onTabChange,
  children,
}: CapturedInformationCardProps) {
  return (
    <Card unconstrained id={capturedInformationCardId}>
      <CardHeader compact bordered>
        <CardTitle cardLayout>
          Captured information
          {showEditButton && (
            <CardHeaderActions>
              <Tooltip content="Edit responses">
                <IconButton
                  name="edit-outlined"
                  label="Edit responses"
                  testId="captured-information-card-edit-responses-button"
                  onPress={onEdit}
                  size={20}
                />
              </Tooltip>
            </CardHeaderActions>
          )}
        </CardTitle>
        <div className={styles.tabs}>
          <TabBar>
            {tabs.map((tab) => (
              <TabItem
                key={tab.id}
                cardLayout
                highlighted={tab.highlighted}
                active={activeTab === tab.id}
                onPress={() => onTabChange(tab.id)}
                testId={tab.testId}
              >
                {tab.label}
              </TabItem>
            ))}
          </TabBar>
        </div>
      </CardHeader>
      <CardContent scroll listContent>
        {children}
      </CardContent>
    </Card>
  )
}

export interface CapturedInformationQuestionListProps {
  children?: ReactNode
}

export function CapturedInformationQuestionList({
  children,
}: CapturedInformationQuestionListProps) {
  return <div>{children}</div>
}

export interface CapturedInformationQuestionProps {
  question: string
  children?: ReactNode
}

export function CapturedInformationQuestion({
  question,
  children,
}: CapturedInformationQuestionProps) {
  return (
    <div className={styles.capturedInfoItem}>
      <div className={styles.capturedInfoLabel}>{question}</div>
      <div className={styles.capturedInfoValue}>{children}</div>
    </div>
  )
}

export interface NotesTabProps {
  label: string
  isRequired: boolean
  isProvided: boolean
  text?: string
  canEdit: boolean
  editButtonLabel: string
  onEdit: () => unknown
  dialog?: ReactNode
}

export function NotesTab({
  label,
  isRequired,
  isProvided,
  text,
  canEdit,
  editButtonLabel,
  onEdit,
  dialog,
}: NotesTabProps) {
  return (
    <div className={styles.noteTabRootContainer}>
      {dialog}
      <FormGroup className={styles.noteTabTextAreaContainer}>
        <label className={styles.noteTabTextAreaLabel}>
          {label}{' '}
          {isRequired && (
            <span className={styles.noteRequiredTag}>*Required</span>
          )}{' '}
          {isProvided && (
            <span className={styles.noteProvidedTag}>Provided</span>
          )}
        </label>
        <TextArea
          rows={5}
          minLength={1}
          placeholder="You don't have any instructions yet."
          value={text}
          data-test="checklist-notes-description"
          isDisabled={true}
        />
      </FormGroup>

      {canEdit && (
        <Button
          label={editButtonLabel}
          size="wide-slim"
          variant="secondary"
          testId="checklist-notes-edit-button"
          onPress={onEdit}
        />
      )}
    </div>
  )
}
